import { ApolloCache, useApolloClient, useMutation } from '@apollo/client';

import { RECORD_USER_SESSION } from '../queries/user-session.queries';
import { recordUserSessionActions, removeUserSessionActions, submitUserSession } from '../utils/user-session.service';
import { actionKey, findAck, isAppliedAck, isSucceededAck } from '../helpers/user-action-ack.helpers';
import { getAuthUser } from './auth.service';

// Writes the edited entity into the normalized cache before the network
// round-trip and returns a rollback to undo it if the submit fails.
export type OptimisticPlayerEdit = (cache: ApolloCache<unknown>) => () => void;

interface IRecordPlayerEditOptions {
  optimisticUpdate?: OptimisticPlayerEdit;
  // True only when the optimistic write is the final state (scalar fields fully
  // known from the request) — then the refetch is skipped. Leave false when the
  // write uses a placeholder the server reconciles, e.g. a temp _id for a new
  // milestone the refetch replaces with the server-assigned record.
  authoritative?: boolean;
}

// Hook — call unconditionally at the top of a component's render.
// Buffers a player edit into the user-session store (offline-resilient, same
// path as scoring) and flushes that one action online. If the device is offline
// the action stays buffered and the OfflineHandler syncs it on reconnect.
// `refetchQueries` refresh the player queries; an authoritative optimistic write
// supersedes them (see IRecordPlayerEditOptions.authoritative).
export const useRecordPlayerEdit = (refetchQueries: string[]) => {
  const user = getAuthUser();
  const client = useApolloClient();
  const [mutation] = useMutation(RECORD_USER_SESSION, { refetchQueries });

  return async (action: IUserAction, { optimisticUpdate, authoritative }: IRecordPlayerEditOptions = {}): Promise<{ error?: Error; refusalReason?: string }> => {
    if (!user) return { error: new Error('Save failed') };

    const rollback = optimisticUpdate?.(client.cache);

    try {
      await recordUserSessionActions(user, [action]);
      const { userSession, recordUserSession } = await submitUserSession(
        user,
        mutation,
        [action],
        optimisticUpdate && authoritative ? { refetchQueries: [] } : undefined,
      );

      // Deleted only on its own acknowledgement; a refusal is recorded server-side, so it leaves the device too.
      const ack = findAck(recordUserSession, action);
      if (userSession && isSucceededAck(ack)) await removeUserSessionActions(user._id, userSession.startedAt, [actionKey(action) as string]);

      if (isAppliedAck(ack)) return {};
      rollback?.();
      // The server names what it refused when it can; the generic line is the fallback.
      const refusalReason = ack?.refusalReason || undefined;
      return { error: new Error(refusalReason || 'Save failed'), refusalReason };
    } catch (err) {
      console.error('recordPlayerEdit failed:', err);
      rollback?.();
      return { error: new Error('Save failed') };
    }
  };
};
